/** Fetch wrapper for the m16-labour-relations API behind the gateway. */

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
  }
}

let authToken: string | null = localStorage.getItem('labour-portal.token');

export function setToken(token: string | null): void {
  authToken = token;
  if (token) localStorage.setItem('labour-portal.token', token);
  else localStorage.removeItem('labour-portal.token');
}

export function getToken(): string | null {
  return authToken;
}

export interface RequestOptions {
  /** Sent as X-Acting-For — the employer mandate an HR-rep persona is acting under (isSelfEmployer() checks in m16's grievances/settlements/osh-incidents/ratio routes). */
  actingFor?: string;
  /** Sent as Idempotency-Key — safe to retry a create call (offline-sync inspection checklist, OSH incident report) without double-creating. Both routes support it per m16's openapi.yaml. */
  idempotencyKey?: string;
}

async function request<T>(method: string, path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
  const headers: Record<string, string> = {};
  if (authToken) headers.authorization = `Bearer ${authToken}`;
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (opts?.actingFor) headers['x-acting-for'] = opts.actingFor;
  if (opts?.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;
  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let payload: unknown = null;
  try {
    payload = text ? JSON.parse(text) : null;
  } catch {
    payload = null;
  }
  if (!res.ok) {
    const err = (payload as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    throw new ApiError(err?.code ?? 'HTTP_ERROR', res.status, err?.message ?? `Request failed (${res.status})`, err?.details);
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>('PUT', path, body, opts),
  del: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
};

/** A fresh key per call site — pass to `opts.idempotencyKey` on offline-sync-prone submits. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
